import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import AddExpenseModal from "./AddExpenseModal";
import ExpenseDetailsModal from "./ExpenseDetailsModal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Plus, Search, Eye, Pencil, Download } from "lucide-react";
import ExpenseCategoryModal from "./ExpenseCategoryModal";

const COLORS = ["#facc15", "#22c55e", "#3b82f6", "#ef4444", "#a855f7"];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    today: 0,
    pending: 0,
    categories: 0,
  });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showCategory, setShowCategory] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [expenseRes, summaryRes, categoryRes] = await Promise.all([
        fetch("/api/expenses", {
          credentials: "include",
        }),
        fetch("/api/expenses/summary", {
          credentials: "include",
        }),
        fetch("/api/expenses/categories", {
          credentials: "include",
        }),
      ]);

      const expenseData = await expenseRes.json();
      const summaryData = await summaryRes.json();
      const categoryData = await categoryRes.json();

      if (expenseData.status === "success") {
        setExpenses(expenseData.data);
      }

      if (summaryData.status === "success") {
        setSummary(summaryData.data);
      }

      if (categoryData.status === "success") {
        setCategories(categoryData.data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      const matchesSearch =
        (e.vendor || "").toLowerCase().includes(search.toLowerCase()) ||
        (e.expense_no || "").toLowerCase().includes(search.toLowerCase());

      const matchesCategory = category === "All" || e.category === category;

      return matchesSearch && matchesCategory;
    });
  }, [expenses, search, category]);

  const chartData = useMemo(() => {
    const map = {};

    filtered.forEach((e) => {
      if (!map[e.category]) {
        map[e.category] = 0;
      }

      map[e.category] += Number(e.amount);
    });

    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
    }));
  }, [filtered]);

  return (
    <DashboardLayout>
      <div className="space-y-10">
        {/* Header */}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif">Expenses</h1>

            <p className="text-sm text-gray-400 mt-1">
              Track operational expenses across branches
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowCategory(true)}
              className="px-5 py-2 rounded-xl border border-white/10"
            >
              Categories
            </button>

            <button
              onClick={() => setShowAdd(true)}
              className="px-5 py-2 rounded-xl bg-amber-400 text-black"
            >
              Add Expense
            </button>
          </div>
        </div>

        {/* KPI */}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <KpiCard
            title="Total Expenses"
            value={`₹${summary.total.toLocaleString()}`}
          />

          <KpiCard
            title="Today's Expense"
            value={`₹${summary.today.toLocaleString()}`}
          />

          <KpiCard
            title="Pending Approval"
            value={`₹${summary.pending.toLocaleString()}`}
            danger
          />

          <KpiCard title="Categories" value={summary.categories} />
        </div>

        {/* Filters */}

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative">
              <Search
                className="absolute left-3 top-3 text-gray-500"
                size={16}
              />

              <input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10"
              />
            </div>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-2 rounded-xl bg-black/40 border border-white/10"
            >
              <option value="All">All</option>

              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>

            <button className="ml-auto px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5 flex items-center gap-2">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Charts */}

        <div className="grid xl:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">
              Expense Category Distribution
            </h3>

            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={chartData} dataKey="value" outerRadius={110} label>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>

                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Expense Overview</h3>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />

                <XAxis dataKey="name" />

                <YAxis />

                <Tooltip />

                <Bar dataKey="value" fill="#facc15" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Expenses Table */}

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-gray-400">
              <tr>
                <th className="text-left p-4">Expense No</th>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Category</th>
                <th className="text-left p-4">Vendor</th>
                <th className="text-left p-4">Branch</th>
                <th className="text-right p-4">Total</th>
                <th className="text-center p-4">Status</th>
                <th className="text-center p-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-400">
                    No expenses found.
                  </td>
                </tr>
              ) : (
                filtered.map((expense) => (
                  <tr
                    key={expense.id}
                    className="border-t border-white/10 hover:bg-white/5 transition"
                  >
                    <td className="p-4 font-medium">{expense.expense_no}</td>

                    <td className="p-4">{expense.date}</td>

                    <td className="p-4">{expense.category}</td>

                    <td className="p-4">{expense.vendor}</td>

                    <td className="p-4">{expense.branch}</td>

                    <td className="p-4 text-right font-semibold text-amber-400">
                      ₹{Number(expense.amount).toLocaleString("en-IN")}
                    </td>

                    <td className="p-4 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          expense.status === "Approved"
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-yellow-500/20 text-yellow-400"
                        }`}
                      >
                        {expense.status}
                      </span>
                    </td>

                    <td className="p-4">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => setSelectedExpense(expense)}
                          className="p-2 rounded-lg hover:bg-white/10"
                        >
                          <Eye size={17} />
                        </button>

                        <button
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowAdd(true);
                          }}
                          className="p-2 rounded-lg hover:bg-white/10"
                        >
                          <Pencil size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}

      {showAdd && (
        <AddExpenseModal
          expense={selectedExpense}
          onClose={() => {
            setShowAdd(false);
            setSelectedExpense(null);
            loadData();
          }}
        />
      )}

      {/* Details Modal */}

      {selectedExpense && !showAdd && (
        <ExpenseDetailsModal
          expense={selectedExpense}
          onClose={() => setSelectedExpense(null)}
        />
      )}

      {showCategory && (
        <ExpenseCategoryModal onClose={() => setShowCategory(false)} />
      )}
    </DashboardLayout>
  );
}

/* ---------- KPI CARD ---------- */

function KpiCard({ title, value, danger }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2
        className={`mt-3 text-3xl font-serif ${
          danger ? "text-red-400" : "text-white"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}
